/**
 * The RFC 7807 ProblemDetails shape the API returns for every failure.
 * `code` is the stable machine-readable identifier the backend's Error type
 * carries; prefer switching on it over matching the human-readable title.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
}

/** Thrown by the API client for any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly correlationId: string | undefined;
  readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(status: number, problem?: ProblemDetails) {
    super(problem?.detail ?? problem?.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem?.code;
    this.correlationId = problem?.correlationId;
    this.fieldErrors = problem?.errors;
  }

  get isValidation(): boolean {
    return this.status === 400 && this.fieldErrors !== undefined;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** 5xx and 429 are worth retrying; a 4xx will fail again the same way. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}
